import PageHeader from "@/components/shared/PageHeader";
import CTASection from "@/components/shared/CTASection";
import { FIVE_PRINCIPLES, THREE_ROLES, AUDIENCES, SITE } from "@/data/content";

export default function About() {
  return (
    <>
      <PageHeader
        overline="About the Centre"
        title="An independent centre, translating the global instruments of resilience."
        lead={`The ${SITE.name} exists to help leaders make sound, evidence-based decisions about climate and disaster resilience and the technology that increasingly underpins it.`}
        image="/about-hero.jpg"
      />

      {/* Mission / Vision */}
      <section className="ccdr-section">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <p className="ccdr-eyebrow mb-5">Mission</p>
            <p className="ccdr-h3 max-w-xl mb-6" style={{ fontWeight: 400, lineHeight: 1.4 }}>
              To help leaders make sound, evidence-based decisions about climate and disaster resilience and the
              technology that increasingly underpins it — by tracking the global frameworks and standards, translating
              them into usable insight, and convening the people who shape and apply them.
            </p>
          </div>
          <div className="lg:col-span-6">
            <p className="ccdr-eyebrow mb-5">Vision</p>
            <p className="ccdr-h3 max-w-xl" style={{ fontWeight: 400, lineHeight: 1.4 }}>
              A world in which resilience is designed deliberately rather than discovered after loss — where the
              instruments of climate adaptation, disaster risk reduction, and responsible technology reinforce one another,
              and where no community is left without protection or voice.
            </p>
          </div>
        </div>
      </section>

      {/* Three roles */}
      <section className="ccdr-section bg-[#F3F4F6] border-y border-[#E5E7EB]">
        <div className="ccdr-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-5">
              <p className="ccdr-eyebrow mb-5">What the Centre does</p>
              <h2 className="ccdr-h2">Three roles, reinforcing each other.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {THREE_ROLES.map((r, idx) => (
              <div key={r.name} className="bg-white border border-[#E5E7EB] p-8">
                <p
                  className="text-xs uppercase tracking-[0.22em] mb-4"
                  style={{ color: "var(--ccdr-accent-dark)" }}
                >
                  Role 0{idx + 1}
                </p>
                <h3 className="ccdr-h3 mb-3">{r.name}</h3>
                <p className="text-[#4B5563]">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Five principles */}
      <section className="ccdr-section">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="ccdr-eyebrow mb-5">Principles</p>
            <h2 className="ccdr-h2">Five short, confident statements.</h2>
            <p className="ccdr-lead mt-6">These are the principles that hold the Centre to account.</p>
          </div>
          <div className="lg:col-span-8 divide-y divide-[#E5E7EB]">
            {FIVE_PRINCIPLES.map((p, idx) => (
              <div key={p.name} className="py-8 grid grid-cols-12 gap-6">
                <div className="col-span-2 lg:col-span-1">
                  <span
                    className="text-2xl"
                    style={{ color: "var(--ccdr-accent-dark)", fontWeight: 300 }}
                  >
                    0{idx + 1}
                  </span>
                </div>
                <div className="col-span-10 lg:col-span-11">
                  <h3 className="ccdr-h3 mb-2">{p.name}</h3>
                  <p className="text-[#4B5563]">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section className="ccdr-section bg-[#053043] text-white">
        <div className="ccdr-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-6">
              <p
                className="text-xs uppercase tracking-[0.22em] mb-5"
                style={{ color: "var(--ccdr-accent)" }}
              >
                Who the Centre serves
              </p>
              <h2 className="ccdr-h2 text-white">Audiences in their own terms.</h2>
            </div>
            <div className="lg:col-span-6 lg:pt-6">
              <p className="text-[#CBD5E1] text-lg leading-relaxed">
                The Centre speaks to leaders who shape decisions about climate and disaster resilience and the
                technology that increasingly underpins it. These readers do not need persuading that risk matters —
                they need a trustworthy, current synthesis they can act on.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1A6B8F]">
            {AUDIENCES.map((a) => (
              <div key={a.slug} className="bg-[#053043] p-10">
                <h3 className="text-white text-xl mb-3">
                  {a.label}
                </h3>
                <p className="text-[#CBD5E1] leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="See how the Centre can support your institution."
        body="Briefings, roundtables, and applied research designed for senior decision makers."
        primary={{ to: "/get-involved", label: "Get involved" }}
      />
    </>
  );
}
